/**
 * `<SubscribeForm />` — public mailing-list intake.
 *
 * Used in two places:
 *   1. Inline below-the-fold on the landing page (`apps/web/pages/index/+Page.tsx`)
 *   2. Dedicated `/subscribe` route (`apps/web/pages/subscribe/+Page.tsx`)
 *
 * Validation runs through the shared `subscribeFormSchema`, plumbed via
 * `@hookform/resolvers/zod` so the same schema feeds both client UX and
 * the server-side `subscribe.create` mutation. Submit calls
 * `subscribeCreate()` (a thin fetch over the public tRPC HTTP transport)
 * and routes the response into Sonner toasts.
 *
 * Accessibility:
 *   - All inputs have visible focus rings (`focus-visible:ring-*`)
 *   - Errors associated via `aria-describedby` + `aria-invalid`
 *   - Submit button shows a busy state via `aria-busy`
 *
 * RTL: relies on `dir="rtl"` on the document root. We use logical
 * properties (gap, padding, text-start) so the layout flips natively
 * for Arabic. Sonner reads the same `dir` attribute and mirrors toasts.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";

import {
  type SubscribeFormValues,
  type SubscriberDigestFrequency,
  type SubscriberLocale,
  subscribeFormSchema,
} from "@hantavirus/shared";

import {
  NetworkError,
  RateLimitError,
  subscribeCreate,
} from "#/lib/subscribe-api.js";
import { type TranslateFn } from "#/lib/i18n.js";
import { getDescriptor, LOCALE_CODES } from "#/lib/i18n/locales.js";
import { notify } from "#/lib/toast.js";

export type SubscribeFormProps = {
  /** Locale used to seed the radio + i18n lookup. */
  locale: SubscriberLocale;
  /** Localised label resolver — namespace `subscribe`. */
  t: TranslateFn;
  /**
   * Test-only override for the API base URL. Not consumed in production.
   */
  apiBaseUrl?: string;
  /**
   * Optional submit override — used by tests to assert the payload
   * without spinning up a real fetch. When supplied, this replaces
   * `subscribeCreate` entirely.
   */
  onSubmitOverride?: (values: SubscribeFormValues) => Promise<{
    status: "sent" | "already-confirmed";
  }>;
};

const FREQS: readonly SubscriberDigestFrequency[] = [
  "daily",
  "weekly",
  "urgent-only",
];

const LOCALES: readonly SubscriberLocale[] = LOCALE_CODES;

export function SubscribeForm({
  locale,
  t,
  apiBaseUrl,
  onSubmitOverride,
}: SubscribeFormProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SubscribeFormValues>({
    resolver: zodResolver(subscribeFormSchema),
    defaultValues: {
      email: "",
      locale,
      digestFrequency: "daily",
    },
    mode: "onSubmit",
  });

  async function onSubmit(values: SubscribeFormValues) {
    try {
      const result = onSubmitOverride
        ? await onSubmitOverride(values)
        : await subscribeCreate(values, { apiBaseUrl });

      if (result.status === "already-confirmed") {
        notify.info(t("toast.alreadyConfirmed"));
      } else {
        notify.success(t("toast.checkInbox"));
        setSubmittedEmail(values.email);
        reset({ email: "", locale, digestFrequency: "daily" });
        setSubmitted(true);
      }
    } catch (err) {
      if (err instanceof RateLimitError) {
        notify.error(t("toast.rateLimited"));
      } else if (err instanceof NetworkError) {
        notify.error(t("toast.genericError"));
      } else {
        notify.error(t("toast.genericError"));
      }
    }
  }

  if (submitted) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col gap-3 rounded-md border-2 border-[color:var(--color-accent-emergency)] bg-[color:var(--color-bg-surface)] p-5 text-[color:var(--color-fg-default)]"
      >
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[color:var(--color-accent-emergency)] text-base font-bold text-[color:var(--color-accent-emergency-fg)]"
          >
            ✓
          </span>
          <div className="flex flex-col gap-1">
            <p className="text-base font-semibold leading-tight">
              {t("success.title")}
            </p>
            <p className="text-sm leading-relaxed text-[color:var(--color-fg-muted)]">
              {submittedEmail
                ? t("success.body", { email: submittedEmail })
                : t("toast.checkInbox")}
            </p>
          </div>
        </div>
        <p className="text-xs leading-relaxed text-[color:var(--color-fg-subtle)]">
          {t("success.spamHint")}
        </p>
        <button
          type="button"
          onClick={() => {
            setSubmitted(false);
            setSubmittedEmail("");
          }}
          className="inline-flex items-center justify-center self-start rounded-md border border-[color:var(--color-border-default)] bg-[color:var(--color-bg-base)] px-3 py-1.5 text-sm text-[color:var(--color-fg-default)] hover:bg-[color:var(--color-bg-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent-emergency)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-bg-base)]"
        >
          {t("success.resubscribe")}
        </button>
      </div>
    );
  }

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      aria-label={t("form.ariaLabel")}
      className="flex flex-col gap-4"
    >
      <div className="flex flex-col gap-1">
        <label
          htmlFor="subscribe-email"
          className="text-sm font-medium text-[color:var(--color-fg-default)]"
        >
          {t("form.email.label")}
        </label>
        <input
          id="subscribe-email"
          type="email"
          autoComplete="email"
          inputMode="email"
          spellCheck={false}
          aria-invalid={errors.email ? "true" : "false"}
          aria-describedby={errors.email ? "subscribe-email-error" : undefined}
          {...register("email")}
          placeholder={t("form.email.placeholder")}
          className="rounded-md border border-[color:var(--color-border-default)] bg-[color:var(--color-bg-surface)] px-3 py-2 text-base text-[color:var(--color-fg-default)] placeholder:text-[color:var(--color-fg-subtle)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent-emergency)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-bg-base)]"
        />
        {errors.email ? (
          <p
            id="subscribe-email-error"
            role="alert"
            className="text-sm text-[color:var(--color-accent-emergency)]"
          >
            {t("form.email.invalid")}
          </p>
        ) : null}
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium text-[color:var(--color-fg-default)]">
          {t("form.locale.label")}
        </legend>
        <div className="flex flex-wrap gap-3">
          {LOCALES.map((code) => (
            <label
              key={code}
              className="inline-flex items-center gap-2 rounded-md border border-[color:var(--color-border-default)] bg-[color:var(--color-bg-surface)] px-3 py-2 text-sm text-[color:var(--color-fg-default)] cursor-pointer focus-within:ring-2 focus-within:ring-[color:var(--color-accent-emergency)]"
            >
              <input
                type="radio"
                value={code}
                {...register("locale")}
                className="accent-[color:var(--color-accent-emergency)] focus-visible:outline-none"
              />
              <span>{getDescriptor(code).nativeLabel}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium text-[color:var(--color-fg-default)]">
          {t("form.frequency.label")}
        </legend>
        <div className="flex flex-wrap gap-3">
          {FREQS.map((freq) => (
            <label
              key={freq}
              className="inline-flex items-center gap-2 rounded-md border border-[color:var(--color-border-default)] bg-[color:var(--color-bg-surface)] px-3 py-2 text-sm text-[color:var(--color-fg-default)] cursor-pointer focus-within:ring-2 focus-within:ring-[color:var(--color-accent-emergency)]"
            >
              <input
                type="radio"
                value={freq}
                {...register("digestFrequency")}
                className="accent-[color:var(--color-accent-emergency)] focus-visible:outline-none"
              />
              <span>{t(`form.frequency.option.${freq}`)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={isSubmitting}
          aria-busy={isSubmitting ? "true" : "false"}
          className="inline-flex items-center justify-center rounded-md border-2 border-[color:var(--color-accent-emergency)] bg-[color:var(--color-accent-emergency)] px-4 py-2 text-base font-semibold text-[color:var(--color-accent-emergency-fg)] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent-emergency)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-bg-base)]"
        >
          {isSubmitting ? t("form.submitting") : t("form.submit")}
        </button>
      </div>

      <p className="text-xs text-[color:var(--color-fg-subtle)]">
        {t("form.privacy")}
      </p>
    </form>
  );
}
